import LectureTrackerPage from './components/LectureTrackerPage.js';
import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import HomePage from './components/HomePage.js';
import ProjectPage from './components/ProjectPage.js';
import { ElephantHuntProvider } from './components/ElephantHunt';
import ScrollToTop from './components/ScrollToTop.js';
import ThemeLab from './components/ThemeLab.js';
import { MouseTrail } from './effects.js';

import CR10 from './assets/CR10.jpg';
import RobotParts from './assets/RobotParts.jpg';
import MotorPID from './assets/MotorPID.jpg';
import Oscilloscope from './assets/Oscilloscope.jpg';
import DoorOpener from './assets/DoorOpener.jpg';
import RobotProto from './assets/RobotProto.png';
import KiantiStorefront from './assets/kianti-storefront.png';
import StatArbHeatmap from './assets/optimization_heatmap_AMAT_NXPI.png';
import StatArbEquityCurve from './assets/kalman_performance_AMAT_NXPI.png';

const featuredProject = {
  id: 'kianti-swim-commerce-platform',
  title: 'Kianti Swim',
  date: 'January 2026 - Present',
  summary: 'A live storefront with custom inventory tools, shipping emails, and newsletter workflows.',
  description: `I designed and built Kianti Swim's online store, including the customer-facing website, inventory tools, automated order emails, and newsletter signup flow. I owned the implementation from the Next.js storefront through the Node.js backend and AWS deployment.`,
  externalUrl: 'https://kiantiswim.com',
  images: [KiantiStorefront],
  caseStudy: [
    { title: 'A storefront connected to daily operations', text: 'The Next.js and React storefront connects to a Node.js backend for order state, business logic, and service integrations.' },
    { title: 'Inventory during live sales', text: 'I built a Retool dashboard for day-to-day stock management. The workflow had to account for operational stock updates happening alongside customer purchases.' },
    { title: 'Shipping events and customer updates', text: 'Resend handles order confirmations and customer emails, while Shippo tracks events feed shipping and delivery updates into the communication flow.' },
    { title: 'Newsletter signup with confirmation', text: 'New subscriber addresses are stored in DynamoDB with a pending status. A confirmation email sent through Resend completes signup, while Mailchimp handles newsletter distribution.' },
    { title: 'Deployment and support', text: 'The platform runs on AWS, with Zoho supporting the customer inbox. The scope includes both the public store and the operational tools behind it.' }
  ],
  stack: 'Next.js / React / Node.js / AWS / DynamoDB / Retool / Resend / Shippo'
};

const projectsData = [
  featuredProject,
  {
    id: 'connect-four-robot',
    title: 'Connect Four Robot',
    date: '2025 - 2026 | Completed',
    summary: 'A physical Connect Four robot with autonomous and remote-control modes.',
    description: `For my completed senior design project, I built a robot that plays a physical game of Connect Four against a human opponent. It supports autonomous play using its own game logic and a remotely controlled mode. A Raspberry Pi Zero 2 W handles processing, with a camera and time-of-flight sensor used to determine the board state. The project brought together robotics, computer vision, and embedded systems.`,
    images: []
  },
    {
    id: 'ar-rubiks-cube-guide',
    summary: 'An AR guide that scans a physical cube and demonstrates the solution on Snapchat Spectacles.',
    title: 'SnapCube',
    date: 'October 2025 - December 2025',
    description: `Using AR-based Snapchat Spectacles, I developed a program that showed users how to solve a Rubik's Cube. The software scans the physical cube one face at a time, utilizes a custom ML model hosted on AWS to determine the solution path, and displays an animated 3D virtual cube that performs the turns. This project combines computer vision, cloud-based machine learning, and real-time AR rendering implementation.`,
    demoVideo: "https://www.youtube.com/embed/foY-Cw0G3e4",
    images: []
  },
  {
    id: 'statistical-arbitrage-engine',
    repositoryUrl: 'https://github.com/KPerlman/statistical-arbitrage-engine',
    summary: 'A Python pairs-trading research pipeline comparing static hedge ratios with a Kalman filter, including transaction costs.',
    title: 'Statistical Arbitrage Engine',
    date: 'June 2025 - July 2025',
    description: `I built a Python research pipeline to screen equity pairs and backtest a statistical arbitrage strategy. It fetches S&P 500 data, runs cointegration tests to identify candidate pairs, and evaluates trades with a transaction cost model.\n\nThe main research question was how a static hedge ratio compares with an adaptive estimate using a Kalman filter. The charts show the AMAT / NXPI backtest and parameter sweep. This project applies ideas from signals and estimation to market data, and reflects my interest in quantitative research and development.`,
    images: [StatArbHeatmap, StatArbEquityCurve]
  },
  {
    id: '3d-printer-modification',
    summary: 'A CR-10 rebuilt with custom firmware, upgraded motion electronics, and direct-drive extrusion.',
    title: '3D Printer Modification',
    date: 'January 2016 - Present',
    description: `3D printers were not only what convinced me to go into Electrical and Computer Engineering in the first place, but have been one of my main interests for over a decade now. Although recently released printers function as consistently as many other household appliances, the Monoprice Mini's, Ender 3's, and CR-10's I have had access to were less of an appliance and more of an ongoing engineering project, requiring constant tinkering.\n\nThe CR-10 was my main tool, which I upgraded with an improved E3D V6 hotend before converting it to an E3D Hemera-based direct drive, a variety of auto-bed-leveling systems, custom printed cooling solutions, an SKR 1.3 board with manually tuned TMC2208 motor drivers, completely rewired internals for a smaller footprint, and countless other modifications. All of this runs on a custom version of Marlin firmware, adjusted to best match the current combination of upgrades.`,
    images: [CR10],
    layout: 'side-by-side'
  },
  {
    id: 'autonomous-balancing-robot',
    summary: 'Early electronics, CAD, and printed wheel designs for a deliberately ambitious household robot.',
    title: 'Balancing Robot Prototype',
    date: 'June 2024 - May 2025',
    description: `This prototype started with an intentionally overambitious idea: a robot that could close doors and cabinets left open around the apartment. I explored the mechanical layout in Fusion 360 and assembled an early electronics prototype using an Arduino Mega, TMC2209 motor drivers, and sensors. I also designed a wheel with a printed TPU tire.

The work shown here covers the prototype hardware and CAD design. Autonomous navigation, docking, and door-closing behavior were goals for a later version, rather than demonstrated capabilities of this prototype.`,
    images: [RobotParts, RobotProto],
    layout: 'side-by-side'
  },
  {
    id: 'door-unlocker',
    summary: 'A phone-controlled door unlocker built with an ESP8266, Raspberry Pi, servo, and printed mounts.',
    title: 'Door Unlocker',
    date: 'October 2024',
    description: `After losing my room key, I was left in a difficult situation due to my apartment door's automatic lock. Fortunately, I had a Raspberry Pi, ESP8266, and servo motor laying around. I battery powered the ESP8266, which was used to send instructions to the servo. These parts were mounted to the door using 3D printed parts I designed in Fusion 360, made to prevent any interference with manual door operation for my roommates. I programmed the Raspberry Pi, located elsewhere, to host a network that my phone could connect to. In doing this I could use the ESP8266's deep sleep feature to increase the battery life with the Raspberry Pi waking it only when necessary for active operation. The final step was creating an Android app in Android Studio that would let me unlock the door from the outside by connecting to the Raspberry Pi.`,
    images: [DoorOpener],
    layout: 'side-by-side'
  },
  {
    id: 'oscilloscope-assembly',
    summary: 'A kit oscilloscope compared with a Tektronix instrument, revealing distortion above 200 kHz.',
    title: 'Oscilloscope Assembly',
    date: 'Oct - Nov 2024',
    description: `For this project, I compared a self-assembled oscilloscope with a professional Tektronix MSO 2024b oscilloscope to measure square, sawtooth, and sinusoidal signals generated in MATLAB at various frequencies. The assembled kit showed noticeable signal distortion and inaccurate amplitude readings above 200 kHz, largely due to its limited sampling rate and display resolution. In contrast, the professional oscilloscope maintained clear, precise waveforms under the same conditions.`,
    images: [Oscilloscope],
    layout: 'side-by-side'
  },
  {
    id: 'custom-dc-motor-pid',
    summary: 'A PIC32 motor controller with PWM output, UART telemetry, and PID tuning in MATLAB.',
    title: 'Custom DC Motor PID',
    date: 'Oct - Nov 2024',
    description: `Using an Explorer 16/32 Board as well as a PIC32MX795F512L, I wrote C and MATLAB code to implement a DC motor PID controller. This involved manually sending binary instructions to the PIC32 to display the motor angle on the board's LCD, using interrupt-based programming to send data over UART to and from MATLAB for gathering data, and generating a PWM signal to control the motor. I then tuned the values to balance rise time against overshoot.`,
    images: [MotorPID],
    layout: 'side-by-side'
  }
];

const themeOptions = [
  { id: 'editorial', label: 'Editorial' },
  { id: 'cosmic', label: 'Cosmic' },
  { id: 'brutalist', label: 'Brutalist' },
  { id: 'sunset', label: 'Sunset Modern' },
  { id: 'swiss', label: 'Swiss Poster' },
  { id: 'museum', label: 'Museum' },
  { id: 'manuscript', label: 'Manuscript' },
  { id: 'paintwash', label: 'Paint Negative' }
];

const DEFAULT_THEME_ID = 'cosmic';

const getInitialTheme = () => {
  if (typeof window === 'undefined') {
    return DEFAULT_THEME_ID;
  }

  const storedTheme = window.localStorage.getItem('portfolio-theme');

  if (themeOptions.some((theme) => theme.id === storedTheme)) {
    return storedTheme;
  }

  return DEFAULT_THEME_ID;
};

function App() {
  const [themeId, setThemeId] = useState(getInitialTheme);
  const [isThemeLabOpen, setIsThemeLabOpen] = useState(false);

  const currentThemeIndex = Math.max(
    0,
    themeOptions.findIndex((theme) => theme.id === themeId)
  );

  const currentTheme = themeOptions[currentThemeIndex];

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', themeId);
    window.localStorage.setItem('portfolio-theme', themeId);
  }, [themeId]);

  const cycleTheme = () => {
    const nextThemeIndex = (currentThemeIndex + 1) % themeOptions.length;
    setThemeId(themeOptions[nextThemeIndex].id);
  };

  /* Standalone lecture tracker */
  if (/^\/lectures(?:\/|$)/.test(window.location.pathname)) {
    return <LectureTrackerPage />;
  }

  return (
    <BrowserRouter>
      <ElephantHuntProvider>
      <ScrollToTop />
      <MouseTrail />
      <ThemeLab
        currentTheme={currentTheme}
        isOpen={isThemeLabOpen}
        onCycle={() => {
          cycleTheme();
          setIsThemeLabOpen(false);
        }}
        onClose={() => setIsThemeLabOpen(false)}
      />
      <Routes>
        <Route path="/" element={<HomePage projects={projectsData} featuredProject={featuredProject} themeId={themeId} />} />
        <Route path="/project/:projectId" element={<ProjectPage projects={projectsData} />} />
      </Routes>
    </ElephantHuntProvider>
    </BrowserRouter>
  );
}

export default App;
